import { createFileRoute } from "@tanstack/react-router";
import Link from "@/components/link";
import { PageHeader } from "@/components/page-header";
import { SkHeader, SkPanel } from "@/components/skeleton";
import { PageQuery } from "@/components/page-query";
import { StaffManager } from "@/features/admin/staff-manager";
import { RolesMatrix } from "@/features/admin/roles-matrix";
import type { AdminPage } from "@shared/pages/admin";
import { pageQuery } from "@/lib/queries";
import { useTitle } from "@/lib/title";

export const Route = createFileRoute("/_app/admin")({
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchQuery(pageQuery<AdminPage>("admin", deps));
  },
  component: AdminRoute,
});

function Skeleton() {
  return (
    <div className="page-shell-narrow">
      <SkHeader withAction={false} />
      <SkPanel bodyClassName="h-40" />
      <SkPanel className="mt-6" bodyClassName="h-56" />
    </div>
  );
}

function AdminRoute() {
  useTitle("Admin");
  const search = Route.useSearch();
  return (
    <PageQuery<AdminPage> name="admin" search={search} skeleton={<Skeleton />}>
      {(page) => <Admin page={page} />}
    </PageQuery>
  );
}

// Team and access. Gated on staff.view; changes need staff.manage or roles.manage.
function Admin({ page }: { page: AdminPage }) {
  return (
    <div className="page-shell-narrow space-y-10">
      <PageHeader
        title="Admin"
        actions={
          page.canAudit ? (
            <Link href="/audit" className="text-xs font-semibold text-accent-dark hover:underline">
              Audit log
            </Link>
          ) : undefined
        }
      />

      <div>
        <h2 className="font-heading text-sm font-bold text-foreground mb-1">Team</h2>
        <p className="text-sm text-muted mb-3">Select a team member to set their role and permissions.</p>
        <StaffManager
          staff={page.staff}
          currentUserId={page.currentUserId}
          canManage={page.canManage}
          canManageRoles={page.canManageRoles}
          roleMatrix={page.matrix}
          overrides={page.overrides}
        />
      </div>

      {page.canManageRoles && (
        <div>
          <h2 className="font-heading text-sm font-bold text-foreground mb-1">Role defaults (advanced)</h2>
          <p className="text-sm text-muted mb-3">
            What each role starts with. Changes made on a team member take precedence. Admin always has full access.
          </p>
          <RolesMatrix initial={page.matrix} canManage={page.canManageRoles} />
        </div>
      )}
    </div>
  );
}
